/**
 * Variable map for quote T&C bodies.
 *
 * The grammar itself is no longer here. `{{path}}` and `{{#if path}}` moved to
 * `rendering/templateRender` in the contracts package when check-in terms
 * gained templating too, so one grammar now serves both surfaces — and, through
 * the parity fixture, the Python resolver in `sm4rt-public-api` as well. What
 * stays here is the part that is genuinely quote-specific: which variables
 * exist and what they resolve to.
 *
 * Values are formatted strings by the time the grammar sees them ("9:00 AM",
 * not `9`; "$1,234.56", not `1234.56`), through the shared formatters so a
 * date renders identically in an emailed quote, on the check-in page, and in
 * the record the public API freezes.
 *
 * The registry of available variables is `QUOTE_TEMPLATE_VARIABLES` in the
 * contracts package — the editor's help panel reads it, and adding a variable
 * means adding an entry there and a resolver here.
 */

import type { AppSettings, BusinessHoursConfig, QuoteDetail, QuoteType } from '@techgitter/contracts';
import {
  BUSINESS_HOURS_DEFAULT,
  businessHoursValues,
  formatCurrencyAud,
  formatLongDate,
  renderTemplateSource,
  type TemplateValues,
} from '@techgitter/contracts';

import { parseLeadTimeUpperDays } from './leadTime';
import { quoteCustomerTotals } from './quoteCustomerTotals';

export interface QuoteTemplateContext {
  appSettings: AppSettings | null;
  quote: QuoteDetail;
  /** Optional system overrides — primarily for tests. */
  system?: {
    companyName?: string;
    today?: Date;
  };
}

const DEFAULT_COMPANY_NAME = 'Tech Solvers';

/**
 * Customer-facing wording for a quote type. Deliberately the same labels the
 * preview puts on a consolidated line — a terms sentence and the line above it
 * should not call the same thing two different names.
 */
const QUOTE_TYPE_LABEL: Record<QuoteType, string> = {
  new_laptop: 'New Laptop',
  new_desktop: 'New Desktop',
  new_desktop_custom: 'New Desktop (Custom)',
  new_all_in_one: 'New All-in-One',
  repair: 'Repair Service',
  network_install: 'Network Installation',
  m365: 'M365 Onboarding',
  replacement: 'New Computer',
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build the flat variable map an interpolation pass walks against.
 * Exposed for tests and the editor's live-preview panel; ``render``
 * calls this internally.
 */
export function buildQuoteTemplateValues(
  ctx: QuoteTemplateContext,
): TemplateValues {
  const hours: BusinessHoursConfig =
    ctx.appSettings?.businessHours ?? BUSINESS_HOURS_DEFAULT;
  const today = ctx.system?.today ?? new Date();
  const companyName = ctx.system?.companyName ?? DEFAULT_COMPANY_NAME;
  const money = quoteCustomerTotals(ctx.quote);

  return {
    // appSettings
    ...businessHoursValues(hours),
    returnGraceDays:
      ctx.appSettings?.returnGraceDays != null
        ? String(ctx.appSettings.returnGraceDays)
        : '',

    // quote
    'quote.number': ctx.quote.number || '',
    'quote.name': (ctx.quote.name || '').trim(),
    'quote.type': QUOTE_TYPE_LABEL[ctx.quote.type] ?? '',
    'quote.validUntil': formatLongDate(ctx.quote.validUntil),
    'quote.leadTime': longestLeadTime(ctx.quote),
    'quote.jobNumber': ctx.quote.rsTicketNumber || '',
    // Money: the figures on the quote the customer is reading, not the raw
    // marked-items sum on the DTO. See `quoteCustomerTotals`.
    'quote.total': formatCurrencyAud(money.total),
    'quote.subtotal': formatCurrencyAud(money.subtotal),
    'quote.totalExGst': formatCurrencyAud(money.totalExGst),
    'quote.gst': formatCurrencyAud(money.gst),
    'quote.totalIncGst': formatCurrencyAud(money.totalIncGst),
    'quote.priceDisplayMode': money.mode === 'inc' ? 'inc GST' : 'ex GST',

    // customer
    'customer.name':
      ctx.quote.customerBusinessName ||
      ctx.quote.customerFullName ||
      ctx.quote.customerName ||
      '',
    'customer.businessName': ctx.quote.customerBusinessName || '',
    'customer.fullName': ctx.quote.customerFullName || '',
    'customer.email': ctx.quote.customerEmail || '',
    'customer.phone': ctx.quote.customerPhone || '',
    'customer.mobile': ctx.quote.customerMobile || '',

    // system
    'system.companyName': companyName,
    'system.today': formatLongDate(formatDateOnly(today)),
  };
}

/**
 * Render a template body against a quote context.
 *
 * Empty / null / undefined ``body`` is a safe pass-through ("" out).
 * Unknown placeholders render empty. Conditional blocks must be
 * balanced; mismatched ``{{#if}}`` / ``{{/if}}`` falls through to the
 * unknown-placeholder rule (the offending block stays in the output
 * verbatim, sans the braces, so the tech can spot it).
 */
export function renderQuoteTemplate(
  body: string | null | undefined,
  ctx: QuoteTemplateContext,
): string {
  if (!body) return '';
  return renderTemplateSource(body, buildQuoteTemplateValues(ctx));
}

// ---------------------------------------------------------------------------
// Quote-specific resolvers
// ---------------------------------------------------------------------------

/**
 * The longest lead time anywhere on the quote.
 *
 * Lead times live on repair-service components — an orderable part carries
 * one, a part already in stock does not — so this is the quote-level twin of
 * `orderablePartsLongestLeadTime` in `utils/repairServiceBlurb.ts`, which answers the
 * same question for a single service.
 *
 * Returns the label as the tech wrote it ("2-3 weeks"), not a parsed number:
 * `parseLeadTimeUpperDays` only ranks them. Empty when nothing on the quote is
 * on order, which leaves `{{#if quote.leadTime}}` blocks out of the terms
 * rather than promising a timeframe nobody entered.
 */
function longestLeadTime(quote: QuoteDetail): string {
  let bestLabel = '';
  let bestDays = 0;

  for (const svc of quote.repairServices ?? []) {
    for (const component of svc.components ?? []) {
      if (component.kind !== 'part' || component.fulfillmentSource === 'stock') {
        continue;
      }
      const label = (component.leadTime ?? '').trim();
      if (!label) continue;
      const days = parseLeadTimeUpperDays(label);
      if (days != null && days > bestDays) {
        bestDays = days;
        bestLabel = label;
      }
    }
  }

  return bestLabel;
}

function formatDateOnly(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}
